'use client';
import { Button } from '@/src/components/button';
import { Container } from '@/src/components/container';
import { SectionHeading } from '@/src/components/section-heading';
import { TextInput } from '@/src/components/inputs/text-input';
import { SmartDatetimeInput } from '@/src/sections/cta/v2/smart-date-time';
import { cn } from '@/src/utils/shadcn';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import * as Yup from 'yup';
export interface CtaSectionProps {
  title: string;
  description: string;
}

const inputCommonClasses = cn(
  'border-white/60 text-white placeholder:text-white/80 focus:border-white dark:border-accent-200 dark:border-opacity-60 dark:text-accent-100 dark:placeholder:text-accent-200/80'
);

const validationSchema = Yup.object({
  name: Yup.string().min(3, 'Name is required').required('Name is required'),
  email: Yup.string().email().required('Email is required'),
});

export function CtaSection({ title, description }: CtaSectionProps) {
  const router = useRouter();
  return (
    <section
      className={cn(
        'relative overflow-hidden bg-primary py-20 dark:bg-accent-900'
      )}
    >
      <Container>
        <div className="relative z-10 grid items-center gap-30px xl:grid-cols-12">
          <div className="text-white dark:text-accent-100 xl:col-span-5">
            <SectionHeading
              title={title}
              description={description}
              size="sm"
              alignment="start"
              textPosition="start"
              invertColor={true}
            />
          </div>
          <div className="xl:col-span-7">
            <Formik
              initialValues={{ name: '', email: '', datetime: null }}
              validationSchema={validationSchema}
              onSubmit={async (values, { resetForm }) => {
                const timezone =
                  Intl.DateTimeFormat().resolvedOptions().timeZone;
                const meetingSchedule = {
                  name: values.name,
                  email: values.email,
                  message: `Requesting meeting at ${values.datetime} , from timezone : ${timezone}`,
                  phoneNo: '',
                  subject: 'Requesting Schedule Meeting',
                };

                const response = await fetch(
                  'https://api.techares.com/api/users/add-contact',
                  {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(meetingSchedule),
                  }
                );

                if (!response.ok) {
                  throw new Error(`HTTP error! Status: ${response.status}`);
                }

                const result = await response.json();
                if (result.data === null) {
                  toast.error(result.message);
                } else {
                  toast.success(result.message);
                  router.push('/thank-you');
                  resetForm();
                }
                resetForm();
              }}
            >
              {({ setFieldValue, isSubmitting }) => (
                <Form className="grid grid-cols-1 gap-30px md:grid-cols-2 xl:max-w-[630px]">
                  <div className="relative">
                    <Field name="name">
                      {/* @ts-ignore */}
                      {({ field }) => (
                        <TextInput
                          {...field}
                          placeholder="Your Name"
                          type="text"
                          className={inputCommonClasses}
                        />
                      )}
                    </Field>
                    <ErrorMessage
                      name="name"
                      component="div"
                      className="absolute text-xs text-red-500"
                    />
                  </div>
                  <div className="relative">
                    <Field name="email">
                      {/* @ts-ignore */}
                      {({ field }) => (
                        <TextInput
                          {...field}
                          placeholder="Your Email"
                          type="email"
                          className={inputCommonClasses}
                        />
                      )}
                    </Field>
                    <ErrorMessage
                      name="email"
                      component="div"
                      className="absolute text-xs text-red-500"
                    />
                  </div>
                  <div className="flex flex-col items-start gap-4 md:col-span-2 md:flex-row md:items-center">
                    <div className="w-full flex-1">
                      <Field name="datetime">
                        {/* @ts-ignore */}
                        {({ field }) => (
                          <SmartDatetimeInput
                            {...field}
                            className={inputCommonClasses}
                            value={field.value || new Date()}
                            onValueChange={(date) =>
                              setFieldValue('datetime', date)
                            }
                            placeholder="e.g. tomorrow at 3pm"
                            disabled={(date) => date < new Date()}
                          />
                        )}
                      </Field>
                    </div>
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className={cn(
                        'w-full flex-none bg-white after:bg-[#003e70] after:text-accent-200 hover:text-accent-200 dark:bg-accent-100 dark:after:bg-accent-800 dark:hover:text-accent-800 sm:flex-1 md:min-w-[190px] md:max-w-[190px]'
                      )}
                    >
                      <span className="text-black">Schedule</span>
                    </Button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </Container>
      <Image
        src="/assets/images/cta/pattern-2.webp"
        alt="cta section bg pattern"
        width={429}
        height={359}
        className="pointer-events-none absolute bottom-0 right-0 z-1 animate-float-bob-x object-cover max-[1650px]:hidden"
        sizes="100vw"
      />
    </section>
  );
}
